import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../auth/useAuth';
import { consumeReauthHint } from '../auth/offlineAuthGrace';
import { consumeSignOutNotice, SIGN_OUT_NOTICE_MESSAGES } from '../auth/signOutNotice';
import { useNavigate } from 'react-router-dom';

const Login = () => {
  const { login, requestPasswordReset } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  // A5: when an offline session expired and bounced the vet here, explain why
  // and reassure that their unsynced work was preserved for re-sync.
  const [reauthHint] = useState(() => consumeReauthHint());
  // P2: a permanent coded 401 (USER_INACTIVE / USER_NOT_PROVISIONED) signed them
  // out. Signing in again would succeed and bounce them straight back, so say
  // what is actually wrong and who can fix it.
  const [signOutNotice] = useState(() => consumeSignOutNotice());

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await login(email, password);
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
    setResetEmail(email);
    setResetSuccess(false);
    setResetError('');
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setResetError('Please enter your email address');
      return;
    }
    setResetLoading(true);
    setResetError('');
    try {
      const response = await requestPasswordReset(resetEmail);
      if (response.success) {
        setResetSuccess(true);
      } else {
        setResetError(response.message || 'Failed to send reset email. Please try again.');
      }
    } catch (err: any) {
      setResetError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
    setResetEmail('');
    setResetSuccess(false);
    setResetError('');
    setResetLoading(false);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Panel */}
      <Box
        sx={{
          flex: isMobile ? 1 : '0 0 50%',
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 3, sm: 6, md: 8 },
          py: 3,
          overflow: 'auto',
        }}
      >
        {/* Top bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
          <Box
            component="img"
            src="/stabletrack-logo-horizontal.png"
            alt="StableTrack"
            sx={{ height: 32 }}
          />
        </Box>

        {/* Centered content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: 400,
            mx: 'auto',
            width: '100%',
          }}
        >
          <Box
            component="img"
            src="/stabletrack-icon.png"
            alt="StableTrack"
            sx={{ height: 72, width: 72, mb: 2 }}
          />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            StableTrack
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4, textAlign: 'center' }}>
            Create an account or log in to manage your equine practice.
          </Typography>

          {signOutNotice && !error && (
            <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
              {SIGN_OUT_NOTICE_MESSAGES[signOutNotice]}
            </Alert>
          )}

          {reauthHint && !error && (
            <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
              You were signed out while offline. Your unsynced changes were saved — sign in to sync
              them.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              placeholder="Enter your email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              size="medium"
            />
            <TextField
              fullWidth
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
              size="medium"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#C9772B',
                '&:hover': { bgcolor: '#6B4727' },
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 1.5,
              }}
            >
              {loading ? 'Signing in...' : 'Continue'}
            </Button>
          </Box>

          <Link
            component="button"
            variant="body2"
            onClick={handleForgotPassword}
            sx={{
              mt: 3,
              textDecoration: 'none',
              color: '#C9772B',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Forgot your password?
          </Link>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Don't have an account?{' '}
              <Link
                component="button"
                onClick={() => navigate('/signup')}
                sx={{
                  textDecoration: 'none',
                  color: '#C9772B',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Sign up
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right Panel - Horse Image */}
      {!isMobile && (
        <Box
          sx={{
            flex: '0 0 50%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src="/a-beautiful-horse-stands-in-a-paddock-behind-a-woo.jpg"
            alt="Horses in paddock"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 5,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
              Modern practice management.
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Everything you need to run your equine clinic efficiently, from scheduling to billing and clinical records.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onClose={handleCloseForgotPassword} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {resetSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Password reset link has been sent to your email. Please check your inbox.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
              {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                margin="normal"
                autoFocus
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {resetSuccess ? (
            <Button onClick={handleCloseForgotPassword} sx={{ color: '#C9772B' }}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseForgotPassword} sx={{ color: '#666' }}>
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={resetLoading}
                sx={{
                  color: '#C9772B',
                  '&:hover': { bgcolor: 'rgba(139,94,52,0.1)' },
                }}
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
